import { Plus, Trash2 } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  NumberStepper,
  useToast,
} from '@/components/ui';
import { useCreateProduct } from '@/hooks/usePriceSettings';
import type { AumBandTemplateRow, CatalogProductRow } from '@/services/priceSettings.service';

const TH = 'text-[10px] font-bold uppercase tracking-wider text-ink/50';
const SLAB_GRID = 'grid grid-cols-[5.5rem_5.5rem_5.5rem_5.5rem_1.75rem] items-center gap-2';

interface SlabDraftRow {
  uid: number;
  aumLower: string;
  aumUpper: string;
  setupPrice: string;
  renewalPrice: string;
}

/**
 * Turns the AUM band template into editable draft rows.
 *
 * Module-level, taking the uid generator as an argument, rather than a closure
 * inside the component: it needs nothing from render scope except a counter, and
 * a nested arrow inside an already-long component body is exactly what pushes
 * the component over the cognitive-complexity budget.
 */
const templateToSlabs = (bands: AumBandTemplateRow[], nextUid: () => number): SlabDraftRow[] =>
  bands.map((band) => ({
    uid: nextUid(),
    aumLower: String(band.aumLowerCrores),
    aumUpper: String(band.aumUpperCrores),
    setupPrice: '',
    renewalPrice: '',
  }));

/** The draft the admin is about to create, as the validators read it. */
interface ProductDraft {
  name: string;
  code: string;
  isAumBased: boolean;
  tier: string;
  price: string;
  slabs: SlabDraftRow[];
}

// Each of these returns the FIRST thing wrong with its slice of the draft, or
// null when that slice is fine. Returning the message rather than calling
// toast.error directly keeps them pure - they are the rules, not the reporting -
// and lets the caller decide how a failure is surfaced.

const tierError = (tier: string, aumProducts: CatalogProductRow[]): string | null => {
  // Optional: not every AUM-priced plan belongs to a graded family. Left blank
  // the plan simply sits outside the ladder. Validated only when the admin
  // actually enters one.
  const raw = tier.trim();
  if (raw === '') return null;

  const parsed = Number(raw);
  if (!Number.isInteger(parsed) || parsed < 1 || parsed > 99) {
    return 'Upgrade tier must be a whole number between 1 and 99';
  }

  const clash = aumProducts.find((product) => product.tier === parsed);
  if (clash) {
    return `Tier ${parsed} is already used by ${clash.name}. Pick a different rank.`;
  }
  return null;
};

const slabRowError = (row: SlabDraftRow, index: number): string | null => {
  const label = `Band ${index + 1}`;
  const lower = Number(row.aumLower);
  const upper = Number(row.aumUpper);
  const setup = Number(row.setupPrice);
  const renewal = Number(row.renewalPrice);

  if (!Number.isFinite(lower) || lower < 0 || row.aumLower === '') {
    return `${label}: enter a valid lower AUM (Cr)`;
  }
  if (!Number.isFinite(upper) || upper <= 0 || upper <= lower) {
    return `${label}: upper AUM must be greater than lower AUM`;
  }
  if (!Number.isFinite(setup) || setup < 0 || !Number.isFinite(renewal) || renewal < 0) {
    return `${label}: enter valid setup and renewal prices`;
  }
  return null;
};

const aumPricingError = (draft: ProductDraft, aumProducts: CatalogProductRow[]): string | null => {
  const tierProblem = tierError(draft.tier, aumProducts);
  if (tierProblem) return tierProblem;

  if (draft.slabs.length === 0) return 'Add at least one AUM band';

  // reduce, not a for-loop with an early return: the first error wins and the
  // rest are ignored either way, and this keeps the band index honest.
  return draft.slabs.reduce<string | null>(
    (found, row, index) => found ?? slabRowError(row, index),
    null,
  );
};

const fixedPricingError = (price: string): string | null => {
  const parsed = Number(price);
  if (!Number.isFinite(parsed) || parsed < 0) return 'Enter a valid annual price';
  return null;
};

/** The first thing wrong with the whole draft, or null when it is valid. */
const firstValidationError = (
  draft: ProductDraft,
  aumProducts: CatalogProductRow[],
): string | null => {
  if (!draft.name.trim() || !draft.code.trim()) return 'Name and code are required';
  if (draft.isAumBased) return aumPricingError(draft, aumProducts);
  return fixedPricingError(draft.price);
};

/**
 * The pricing half of the confirmation sentence.
 *
 * A named function rather than the nested ternary this used to be inline in the
 * JSX: it is the last thing an admin reads before a catalogue entry is created,
 * and each branch being its own return is what makes it checkable at a glance.
 */
const pricingSummary = (draft: ProductDraft): string => {
  if (!draft.isAumBased) {
    return ` - fixed price ₹${Number(draft.price).toLocaleString('en-IN')}.`;
  }
  const bands = `${draft.slabs.length} band${draft.slabs.length === 1 ? '' : 's'}`;
  const ladderPlace =
    draft.tier.trim() === ''
      ? 'not part of an upgrade ladder'
      : `at upgrade tier ${draft.tier.trim()}`;
  return ` - AUM-based with ${bands}, ${ladderPlace}.`;
};

interface AddProductDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bandTemplate: AumBandTemplateRow[];
  /** Live AUM plans, for the tier ladder: which rungs are taken. */
  aumProducts: CatalogProductRow[];
  /**
   * Seeds the code box when the dialog opens. Used by "Add website tier", which
   * prefills the WEBSITE_ prefix - that prefix is what makes the new product
   * behave as a website (one live at a time, price picked from admin options),
   * so leaving the admin to remember it would quietly create a plain product.
   */
  initialCode?: string;
}

export const AddProductDialog = ({
  open,
  onOpenChange,
  bandTemplate,
  aumProducts,
  initialCode = '',
}: AddProductDialogProps) => {
  const create = useCreateProduct();
  const { toast } = useToast();
  const aumCheckboxId = useId();
  const noDiscountCheckboxId = useId();
  const uidCounter = useRef(0);

  const freshUid = (): number => {
    uidCounter.current += 1;
    return uidCounter.current;
  };

  const [name, setName] = useState('');
  const [code, setCode] = useState(initialCode);
  const [isAumBased, setIsAumBased] = useState(false);
  const [noDiscount, setNoDiscount] = useState(false);
  // Rank in the MFBOX upgrade ladder (higher is better). AUM plans only - they
  // are mutually exclusive alternatives, which is what makes upgrading between
  // them meaningful. Blank = this plan takes no part in the ladder.
  const [tier, setTier] = useState('');
  const [price, setPrice] = useState('');
  const [slabs, setSlabs] = useState<SlabDraftRow[]>(() => templateToSlabs(bandTemplate, freshUid));
  const [showConfirm, setShowConfirm] = useState(false);

  // The existing ladder, lowest rung first, so the admin picks a rank in
  // context instead of discovering clashes by trial and error.
  const ladder = aumProducts
    .filter((p): p is CatalogProductRow & { tier: number } => p.tier != null)
    .sort((a, b) => a.tier - b.tier);
  const untieredAum = aumProducts.filter((p) => p.tier == null);
  // Next free rung above the current top - the usual intent when adding a plan.
  const suggestedTier = (ladder.at(-1)?.tier ?? 0) + 1;

  useEffect(() => {
    if (!open) return;
    setSlabs(templateToSlabs(bandTemplate, freshUid));
    setShowConfirm(false);
    // Reseeded on every open, not just on mount: the dialog instance is reused,
    // so opening it from "Add website tier" after an ordinary "Add product"
    // would otherwise keep the previous, unprefixed code.
    setCode(initialCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bandTemplate, initialCode]);

  const resetForm = (): void => {
    setName('');
    setCode('');
    setIsAumBased(false);
    setNoDiscount(false);
    setTier('');
    setPrice('');
    setSlabs(templateToSlabs(bandTemplate, freshUid));
    setShowConfirm(false);
  };

  const setSlabField = (
    uid: number,
    field: keyof Omit<SlabDraftRow, 'uid'>,
    value: string,
  ): void => {
    setSlabs((prev) => prev.map((row) => (row.uid === uid ? { ...row, [field]: value } : row)));
  };

  const addSlabRow = (): void => {
    setSlabs((prev) => [
      ...prev,
      {
        uid: freshUid(),
        aumLower: '',
        aumUpper: '',
        setupPrice: '',
        renewalPrice: '',
      },
    ]);
  };

  const deleteSlabRow = (uid: number): void => {
    setSlabs((prev) => prev.filter((row) => row.uid !== uid));
  };

  const draft: ProductDraft = {
    name,
    code,
    isAumBased,
    tier,
    price,
    slabs,
  };

  const handleSubmitClick = (): void => {
    const problem = firstValidationError(draft, aumProducts);
    if (problem) {
      toast.error(problem);
      return;
    }
    setShowConfirm(true);
  };

  const handleConfirm = (): void => {
    const trimmedName = name.trim();
    const trimmedCode = code.trim();

    if (isAumBased) {
      create.mutate(
        {
          code: trimmedCode,
          name: trimmedName,
          isAumBased: true,
          noDiscount,
          tier: tier.trim() === '' ? null : Number(tier.trim()),
          slabs: slabs.map((row) => ({
            aumLowerCrores: Number(row.aumLower),
            aumUpperCrores: Number(row.aumUpper),
            setupPrice: Math.round(Number(row.setupPrice)),
            renewalPrice: Math.round(Number(row.renewalPrice)),
          })),
        },
        {
          onSuccess: () => {
            toast.success('Product created');
            resetForm();
            onOpenChange(false);
          },
          onError: (err) => {
            toast.error(err.message || 'Could not create product');
            setShowConfirm(false);
          },
        },
      );
      return;
    }

    create.mutate(
      {
        code: trimmedCode,
        name: trimmedName,
        isAumBased: false,
        noDiscount,
        // Not Math.round (ADKJ-124): a new product's price can be
        // fractional (e.g. "Website (AI based)" at 3093.2203), and rounding
        // it away here would defeat the whole point of that catalogue entry.
        price: Number(price),
      },
      {
        onSuccess: () => {
          toast.success('Product created');
          resetForm();
          onOpenChange(false);
        },
        onError: (err) => {
          toast.error(err.message || 'Could not create product');
          setShowConfirm(false);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add product</DialogTitle>
          <DialogDescription>
            Add a fixed-price catalogue item or an AUM-based product with custom slab bands.
          </DialogDescription>
        </DialogHeader>

        {showConfirm ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm">
            <p className="font-semibold text-amber-800">Confirm new product</p>
            <p className="mt-1.5 text-amber-700">
              {'You are about to create '}
              <strong>{code.trim().toUpperCase()}</strong>
              {` "${name.trim()}"`}
              {pricingSummary(draft)}
              {noDiscount ? ' Discounts will be disabled for this product.' : ''}
            </p>
            <p className="mt-1.5 text-amber-600 text-[11px]">
              This product can be deactivated later from the product settings if needed.
            </p>
          </div>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label="Product name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. MFTools Pro"
              />
              <Input
                label="Product code"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase().replace(/\s+/g, '_'))}
                placeholder="e.g. MFTOOLS_PRO"
                helperText="Uppercase letters, numbers, and underscores"
              />
            </div>

            <div className="flex flex-col gap-2.5">
              <label
                htmlFor={aumCheckboxId}
                className="flex cursor-pointer items-center gap-2 text-sm font-medium text-ink"
              >
                <input
                  id={aumCheckboxId}
                  type="checkbox"
                  checked={isAumBased}
                  onChange={(e) => setIsAumBased(e.target.checked)}
                  className="h-4 w-4 rounded border-line text-brand focus:ring-brand/30"
                />
                <span>AUM-based pricing (slab setup + renewal)</span>
              </label>
              <label
                htmlFor={noDiscountCheckboxId}
                className="flex cursor-pointer items-center gap-2 text-sm font-medium text-ink"
              >
                <input
                  id={noDiscountCheckboxId}
                  type="checkbox"
                  checked={noDiscount}
                  onChange={(e) => setNoDiscount(e.target.checked)}
                  className="h-4 w-4 rounded border-line text-brand focus:ring-brand/30"
                />
                <span>Do not allow discounts</span>
              </label>
            </div>

            {isAumBased && (
              <div className="rounded-lg border border-line bg-canvas p-3">
                <Input
                  label="Upgrade tier (optional)"
                  type="number"
                  min={1}
                  max={99}
                  step={1}
                  value={tier}
                  onChange={(e) => setTier(e.target.value)}
                  placeholder={`e.g. ${suggestedTier}`}
                  className="max-w-40"
                  helperText="Rank in a graded plan family - higher is better. A client on a lower tier can be upgraded to this plan."
                />
                {/* The consequence of leaving it blank, stated up front: it is
                    a real choice, not an omission, and it is not otherwise
                    visible anywhere until someone tries to sell an upgrade. */}
                {tier.trim() === '' && (
                  <p className="mt-1.5 text-[11px] text-ink/60">
                    Leave blank if this plan is not part of a graded family. It will be sold and
                    renewed normally, but never offered as an upgrade - and clients on it cannot be
                    upgraded to another plan.
                  </p>
                )}
                <div className="mt-2 border-t border-line pt-2">
                  <p className="text-[11px] font-semibold text-ink/70">Ladder so far</p>
                  {ladder.length === 0 ? (
                    <p className="mt-0.5 text-[11px] text-ink/50">
                      No tiered MFBOX plans yet - this will be the first rung.
                    </p>
                  ) : (
                    <ul className="mt-1 space-y-0.5">
                      {ladder.map((p) => (
                        <li key={p.code} className="text-[11px] text-ink/60">
                          {`Tier ${p.tier} · ${p.name}`}
                        </li>
                      ))}
                    </ul>
                  )}
                  {untieredAum.length > 0 && (
                    <p className="mt-1.5 text-[11px] text-ink/50">
                      {`Not in any ladder: ${untieredAum.map((p) => p.name).join(', ')}.`}
                    </p>
                  )}
                </div>
              </div>
            )}

            {!isAumBased ? (
              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium text-ink">Annual price (₹)</span>
                <NumberStepper
                  value={Number(price) || 0}
                  onChange={(v) => setPrice(String(v))}
                  step={500}
                  hideZero
                  inputWidthClassName="w-24"
                  placeholder="0"
                  ariaLabel="Annual price"
                />
              </div>
            ) : (
              <div className="overflow-hidden rounded-lg border border-line">
                <div className="flex items-center justify-between gap-3 border-b border-line bg-canvas px-3 py-2">
                  <div>
                    <p className="text-xs font-semibold text-ink">AUM slab prices</p>
                    <p className="text-[11px] text-ink/50">
                      Edit band bounds and prices. Add or remove bands as needed.
                    </p>
                  </div>
                  <Button size="sm" variant="outline" onClick={addSlabRow} className="shrink-0">
                    <Plus className="mr-1 h-3.5 w-3.5" />
                    Add band
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  {/* No role="row": this is a CSS grid, not a table, and a row
                      role with no table/grid/rowgroup ancestor is not exposed as
                      one by any screen reader - it only looked like semantics.
                      Each field below carries its own aria-label, which is what
                      actually names the controls. */}
                  <div className={`${SLAB_GRID} border-b border-line bg-canvas px-3 py-2.5`}>
                    <span className={TH}>Lower (Cr)</span>
                    <span className={TH}>Upper (Cr)</span>
                    <span className={`${TH} text-right`}>Setup (₹)</span>
                    <span className={`${TH} text-right`}>Renewal (₹, annual)</span>
                    <span />
                  </div>
                  <div className="max-h-56 divide-y divide-line overflow-y-auto">
                    {slabs.length === 0 ? (
                      <p className="px-3 py-4 text-center text-sm text-ink/50">
                        No bands yet - click &ldquo;Add band&rdquo; to begin.
                      </p>
                    ) : (
                      slabs.map((row) => (
                        <div key={row.uid} className={`${SLAB_GRID} px-3 py-2`}>
                          <input
                            type="number"
                            min={0}
                            step={1}
                            value={row.aumLower}
                            onChange={(e) => setSlabField(row.uid, 'aumLower', e.target.value)}
                            className="w-full rounded-md border border-line bg-white px-2 py-1.5 text-sm shadow-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                            aria-label="Lower AUM (Cr)"
                            placeholder="0"
                          />
                          <input
                            type="number"
                            min={0}
                            step={1}
                            value={row.aumUpper}
                            onChange={(e) => setSlabField(row.uid, 'aumUpper', e.target.value)}
                            className="w-full rounded-md border border-line bg-white px-2 py-1.5 text-sm shadow-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                            aria-label="Upper AUM (Cr)"
                            placeholder="5"
                          />
                          <input
                            type="number"
                            min={0}
                            step={500}
                            value={row.setupPrice}
                            onChange={(e) => setSlabField(row.uid, 'setupPrice', e.target.value)}
                            className="w-full rounded-md border border-line bg-white px-2 py-1.5 text-right text-sm shadow-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                            aria-label="Setup price (₹)"
                            placeholder="0"
                          />
                          <input
                            type="number"
                            min={0}
                            step={500}
                            value={row.renewalPrice}
                            onChange={(e) => setSlabField(row.uid, 'renewalPrice', e.target.value)}
                            className="w-full rounded-md border border-line bg-white px-2 py-1.5 text-right text-sm shadow-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                            aria-label="Renewal price (annual, ₹)"
                            placeholder="0"
                          />
                          <button
                            type="button"
                            onClick={() => deleteSlabRow(row.uid)}
                            className="flex h-6 w-6 items-center justify-center rounded text-ink/30 hover:bg-red-50 hover:text-red-500 focus:outline-none"
                            aria-label="Delete band"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        <DialogFooter>
          {showConfirm ? (
            <>
              <Button
                variant="ghost"
                onClick={() => setShowConfirm(false)}
                disabled={create.isPending}
              >
                Back
              </Button>
              <Button onClick={handleConfirm} disabled={create.isPending}>
                {create.isPending ? 'Creating…' : 'Confirm create'}
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={handleSubmitClick}>Create product</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
